import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Calendar, 
  DollarSign, 
  Users, 
  Clock,
  FileText,
  Plus,
  MoreVertical 
} from 'lucide-react';
import { useProject } from '@/hooks/useProjects';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';

// We'll create these components next
import ProjectMilestones from '@/components/project/ProjectMilestones';
import ProjectExpenses from '@/components/project/ProjectExpenses';
import ProjectTeamMembers from '@/components/project/ProjectTeamMembers';
import ProjectTimeEntries from '@/components/project/ProjectTimeEntries';
import ProjectActivities from '@/components/project/ProjectActivities';
import ProjectIntegrations from '@/components/project/ProjectIntegrations';

export default function ProjectDetails() {
  const { id } = useParams<{ id: string }>();
  const { data: project, isLoading } = useProject(id!);
  const [activeTab, setActiveTab] = useState('overview');

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-[300px]" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-[100px]" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-[120px]" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Project Not Found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              The project you're looking for doesn't exist or has been deleted.
            </p>
            <Link to="/projects">
              <Button>Back to Projects</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'on_hold':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/projects">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{project.name}</h1>
              {project.project_code && (
                <Badge variant="outline">{project.project_code}</Badge>
              )}
              <Badge className={getStatusColor(project.status)}>
                {project.status || 'draft'}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              {project.organization?.name || 'No Organization'}
              {project.project_manager && (
                <span> • PM: {project.project_manager.first_name} {project.project_manager.last_name}</span>
              )}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Link to={`/projects/${project.id}/edit`}>
            <Button variant="outline">
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>Export Report</DropdownMenuItem>
              <DropdownMenuItem>Archive Project</DropdownMenuItem>
              <DropdownMenuItem className="text-red-600">Delete Project</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Budget</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(project.budget || project.value)}</div>
            {project.progress?.completed_revenue && (
              <p className="text-xs text-muted-foreground">
                {formatCurrency(project.progress.completed_revenue)} earned
              </p>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Progress</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {project.progress?.progress_percentage?.toFixed(0) || 0}%
            </div>
            <p className="text-xs text-muted-foreground">
              {project.progress?.completed_milestones || 0} of {project.progress?.total_milestones || 0} milestones
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Team</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{project.team_member_count || 0}</div>
            <p className="text-xs text-muted-foreground">Active members</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Time Logged</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{project.total_time_logged?.toFixed(1) || 0}h</div>
            <p className="text-xs text-muted-foreground">
              {project.profitability?.total_time_cost ? formatCurrency(project.profitability.total_time_cost) : '$0'} cost
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Profitability Summary */}
      {project.profitability && (
        <Card>
          <CardHeader>
            <CardTitle>Profitability Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-5">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Revenue</p>
                <p className="text-xl font-bold text-green-600">
                  {formatCurrency(project.profitability.total_revenue)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Expenses</p>
                <p className="text-xl font-bold text-red-600">
                  {formatCurrency(project.profitability.total_expenses)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Time Cost</p>
                <p className="text-xl font-bold text-orange-600">
                  {formatCurrency(project.profitability.total_time_cost)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Net Profit</p>
                <p className={`text-xl font-bold ${project.profitability.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(project.profitability.net_profit)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Profit Margin</p>
                <p className={`text-xl font-bold ${project.profitability.profit_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {project.profitability.profit_margin.toFixed(1)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Project Details */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Project Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {project.description && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Description</p>
                <p className="text-sm">{project.description}</p>
              </div>
            )}
            
            <div className="grid gap-4 md:grid-cols-2">
              {project.start_date && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Start Date</p>
                  <p className="text-sm">{format(new Date(project.start_date), 'MMM d, yyyy')}</p>
                </div>
              )}
              
              {project.end_date && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">End Date</p>
                  <p className="text-sm">{format(new Date(project.end_date), 'MMM d, yyyy')}</p>
                </div>
              )}
            </div>
            
            <div>
              <p className="text-sm font-medium text-muted-foreground">Type</p>
              <p className="text-sm capitalize">{project.type}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {project.recent_activities && project.recent_activities.length > 0 ? (
              <div className="space-y-3">
                {project.recent_activities.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{activity.title}</p>
                      <p className="text-sm text-muted-foreground">{activity.description}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(activity.activity_date), 'MMM d, h:mm a')}
                      </p>
                    </div>
                  </div>
                ))}
                <Link to={`/projects/${project.id}/activities`}>
                  <Button variant="link" className="p-0 h-auto">
                    View all activities →
                  </Button>
                </Link>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No recent activity</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detailed Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="milestones">Milestones</TabsTrigger>
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
          <TabsTrigger value="time">Time Tracking</TabsTrigger>
          <TabsTrigger value="activities">Activities</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Upcoming Milestones</CardTitle>
              </CardHeader>
              <CardContent>
                <ProjectMilestones projectId={project.id} limit={3} />
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Recent Expenses</CardTitle>
              </CardHeader>
              <CardContent>
                <ProjectExpenses projectId={project.id} limit={3} />
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="milestones">
          <ProjectMilestones projectId={project.id} />
        </TabsContent>
        
        <TabsContent value="expenses">
          <ProjectExpenses projectId={project.id} />
        </TabsContent>
        
        <TabsContent value="team">
          <ProjectTeamMembers projectId={project.id} />
        </TabsContent>
        
        <TabsContent value="time">
          <ProjectTimeEntries projectId={project.id} />
        </TabsContent>
        
        <TabsContent value="activities">
          <ProjectActivities projectId={project.id} />
        </TabsContent>
        
        <TabsContent value="integrations">
          <ProjectIntegrations projectId={project.id} organizationId={project.organization_id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}